import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/content/blog/${slug}`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/content/blog`).then(r => r.json()),
    ]).then(([p, all]) => {
      setPost(p);
      setAllPosts(all);
    }).catch(console.error).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-700" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Post not found</h2>
          <Link to="/blog"><Button className="bg-emerald-800 hover:bg-emerald-900">Back to Blog</Button></Link>
        </div>
      </div>
    );
  }

  const relatedPosts = allPosts
    .filter(p => p.category === post.category && p.slug !== post.slug)
    .slice(0, 3);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, text: post.excerpt, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="relative h-[60vh] flex items-end">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${post.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 pb-12 text-white">
          <div className="inline-block bg-amber-600 text-white px-3 py-1 text-sm font-semibold mb-4 rounded-sm">{post.category}</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-6">{post.title}</h1>
          <div className="flex items-center gap-6 text-white/90">
            <span className="flex items-center gap-2"><User className="w-5 h-5" />{post.author}</span>
            <span className="flex items-center gap-2"><Calendar className="w-5 h-5" />{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="flex items-center gap-2"><Clock className="w-5 h-5" />{post.readTime}</span>
          </div>
        </div>
      </section>

      <div className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/blog"><Button variant="ghost" className="hover:bg-zinc-100"><ArrowLeft className="mr-2 w-4 h-4" /> Back to Blog</Button></Link>
          <Button variant="outline" onClick={handleShare} className="border-2 border-zinc-300 hover:border-emerald-800"><Share2 className="mr-2 w-4 h-4" /> Share</Button>
        </div>
      </div>

      <article className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg prose-zinc max-w-none">
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="text-4xl font-serif mb-6 mt-12 text-zinc-900" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-3xl font-serif mb-4 mt-10 text-zinc-900" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-2xl font-serif mb-3 mt-8 text-zinc-900" {...props} />,
                p: ({node, ...props}) => <p className="text-lg text-zinc-700 leading-relaxed mb-6" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-6 space-y-2 text-zinc-700" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-6 space-y-2 text-zinc-700" {...props} />,
                li: ({node, ...props}) => <li className="text-lg" {...props} />,
                strong: ({node, ...props}) => <strong className="font-semibold text-zinc-900" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-emerald-700 pl-6 my-8 italic text-zinc-600" {...props} />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section className="py-16 px-4 bg-zinc-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl font-serif mb-2 text-zinc-900">Related Articles</h2>
              <div className="w-20 h-1 bg-amber-600" />
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.post_id || relatedPost.slug} to={`/blog/${relatedPost.slug}`}>
                  <Card className="group overflow-hidden hover:shadow-xl transition-all duration-500 border-0 rounded-sm h-full">
                    <div className="relative h-48 overflow-hidden">
                      <img src={relatedPost.image} alt={relatedPost.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <CardContent className="p-6 bg-white">
                      <h3 className="text-xl font-serif text-zinc-900 mb-2 group-hover:text-emerald-800 transition-colors line-clamp-2">{relatedPost.title}</h3>
                      <p className="text-zinc-600 text-sm line-clamp-2">{relatedPost.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 px-4 bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif mb-6">Ready to Start Your Adventure?</h2>
          <p className="text-xl mb-10 text-white/90">Let us help you plan your perfect Himalayan journey</p>
          <div className="flex gap-6 justify-center flex-wrap">
            <Link to="/build-adventure"><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg rounded-sm transition-all duration-300 hover:scale-105">Build Your Adventure</Button></Link>
            <Link to="/"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-emerald-900 px-10 py-6 text-lg rounded-sm transition-all duration-300">Explore Tours</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
